import { useCallback, useEffect, useRef, useState } from 'react';
import axios from '../utils/axios';

export async function validatePrintRunOutput(organizationId: number, printRunId: number): Promise<void> {
  await axios.post('/printrun/validate-output', null, { params: { organizationId, printRunId } });
}
import {
  DEFAULT_LIST_PAGE,
  DEFAULT_LIST_PAGE_SIZE,
} from '../constants';
import { mapPrintRuns } from '../utils/ridMappers';
import { throwIfLegacyResultError } from '../utils/requestErrors';
import { addDurablePrintRun, type PrintRequestOptions } from './printingRequests';
export { acknowledgeCompletedPrintRun, getPendingPrintIntent, resumePendingPrintRun,
  PRINT_INTENT_CHANGED, PrintingPendingError } from './printingRequests';
import type {
  PagedResult,
  PrintRunRecord,
  PrintRunTotalsParams,
} from '../types/index';

const draftDeletionRequests = new Map<string, Promise<unknown>>();

export type DeleteDraftPrintRun = (printRunId: number) => Promise<void>;

export type UseDeleteDraftPrintRunResult = {
  deleteDraft: DeleteDraftPrintRun;
  isDeletingDraft: boolean;
};

function normalizePrintRuns(printRuns: any[] = []): PrintRunRecord[] {
  return mapPrintRuns(printRuns || []) as PrintRunRecord[];
}

export async function getPrintRuns(
  organizationId: number | string,
  page = DEFAULT_LIST_PAGE,
  pageSize: number = DEFAULT_LIST_PAGE_SIZE
): Promise<PrintRunRecord[]> {
  const response = await axios.get('/printrun', {
    params: { organizationId, page, pageSize },
  });
  return normalizePrintRuns(Array.isArray(response.data) ? response.data : []);
}

export async function getPrintRunById(
  organizationId: number | string,
  printRunId: number | string
): Promise<PrintRunRecord | null> {
  const response = await axios.get('/printrun/byid', {
    params: { organizationId, printRunId },
  });
  if (!response.data || typeof response.data !== 'object') {
    return null;
  }
  const mapped = normalizePrintRuns([response.data]);
  return mapped[0] || null;
}

export type UsePrintRunByIdOptions = {
  /** Already-loaded record (e.g. router state) used to render immediately. */
  initialData?: PrintRunRecord | null;
  enabled?: boolean;
  /** Editing must wait for the authorized detail response, even with router-state data. */
  requireAuthoritative?: boolean;
};

export type UsePrintRunByIdResult = {
  data: PrintRunRecord | null;
  isLoading: boolean;
  error: unknown;
  retry: () => void;
};

/**
 * Owns the by-ID request lifecycle for a restored print run. List responses omit `qrs`,
 * so initial data only seeds the first render and the authoritative record is still loaded once per key.
 */
export function usePrintRunById(
  organizationId?: number | string | null,
  printRunId?: number | string | null,
  options: UsePrintRunByIdOptions = {},
): UsePrintRunByIdResult {
  const { initialData = null, enabled = true, requireAuthoritative = false } = options;
  const [reloadToken, setReloadToken] = useState(0);
  const retry = useCallback(() => setReloadToken((token) => token + 1), []);

  const initialDataRef = useRef<PrintRunRecord | null>(initialData);
  initialDataRef.current = initialData;

  const hasOrganization = organizationId !== null && organizationId !== undefined && organizationId !== '';
  const hasPrintRun = printRunId !== null && printRunId !== undefined && printRunId !== '';
  const shouldFetch = enabled && hasOrganization && hasPrintRun;
  const waitingForOrganization = enabled && requireAuthoritative && hasPrintRun && !hasOrganization;

  // Identity of the record being resolved; a result is only ever exposed for the current key.
  const requestKey = JSON.stringify([
    String(organizationId ?? ''),
    String(printRunId ?? ''),
    shouldFetch,
    requireAuthoritative,
    reloadToken,
  ]);

  const [state, setState] = useState<{
    key: string | null;
    data: PrintRunRecord | null;
    error: unknown;
    isLoading: boolean;
  }>({ key: null, data: null, error: null, isLoading: false });

  useEffect(() => {
    const seed = initialDataRef.current;

    if (!shouldFetch) {
      setState({ key: requestKey, data: requireAuthoritative ? null : seed, error: null, isLoading: waitingForOrganization });
      return;
    }

    let cancelled = false;
    setState({ key: requestKey, data: requireAuthoritative ? null : seed, error: null, isLoading: requireAuthoritative || !seed });

    getPrintRunById(organizationId as number | string, printRunId as number | string)
      .then((result) => {
        if (cancelled) return;
        // Keep the seeded record when the lookup yields nothing.
        setState({ key: requestKey, data: result ?? (requireAuthoritative ? null : seed) ?? null, error: null, isLoading: false });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ key: requestKey, data: requireAuthoritative ? null : seed ?? null, error: err, isLoading: false });
      });

    return () => {
      cancelled = true;
    };
  }, [requestKey, shouldFetch, organizationId, printRunId, requireAuthoritative, waitingForOrganization]);

  const matchesCurrentKey = state.key === requestKey;

  return {
    data: matchesCurrentKey ? state.data : requireAuthoritative ? null : initialData,
    error: matchesCurrentKey ? state.error : null,
    isLoading: matchesCurrentKey ? state.isLoading : waitingForOrganization || (shouldFetch && (requireAuthoritative || !initialData)),
    retry,
  };
}

export async function getPrintRunsTotals({
  organizationId,
  searchTerm = null,
  status = null,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LIST_PAGE_SIZE,
  sortField = null,
  sortAscending = null,
}: PrintRunTotalsParams = {}): Promise<PagedResult<PrintRunRecord>> {
  const response = await axios.get('/printrun/totals', {
    params: {
      organizationId,
      search: searchTerm,
      status,
      page,
      pageSize,
      ...(sortField === null || sortField === undefined ? {} : { sortField }),
      ...(sortAscending === null || sortAscending === undefined ? {} : { sortAscending }),
    },
  });

  return {
    page: Number(response.data?.page ?? page),
    pageSize: Number(response.data?.pageSize ?? pageSize),
    total: Number(response.data?.total ?? 0),
    data: normalizePrintRuns(Array.isArray(response.data?.data) ? response.data.data : []),
  };
}

export async function addPrintRun(payload: unknown, options?: PrintRequestOptions): Promise<any> {
  return addDurablePrintRun(payload, options);
}

export async function editPrintRun(payload: unknown): Promise<PrintRunRecord> {
  const response = await axios.post('/printrun/edit', payload);
  if (
    !response.data
    || typeof response.data !== 'object'
    || Array.isArray(response.data)
    || response.data.error === true
  ) {
    throw new Error('Print run edit did not return the updated record.');
  }

  const [updatedPrintRun] = normalizePrintRuns([response.data]);
  const updatedPrintRunId = Number(updatedPrintRun?.id);
  if (!updatedPrintRun || !Number.isSafeInteger(updatedPrintRunId) || updatedPrintRunId <= 0) {
    throw new Error('Print run edit did not return the updated record.');
  }

  return updatedPrintRun;
}

export async function reassignPrintRunProduct(payload: {
  organizationId: number | string;
  printRunId: number;
  productOverride: number;
}): Promise<PrintRunRecord> {
  const response = await axios.post('/printrun/reassign-product', payload);
  const data = response?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data) || data.error === true) {
    throw new Error('Product reassignment did not return the updated record.');
  }
  const [updated] = normalizePrintRuns([data]);
  if (Number(updated?.id) !== Number(payload.printRunId)
    || Number(updated?.organizationId) !== Number(payload.organizationId)
    || Number(updated?.productOverride) !== Number(payload.productOverride)) {
    throw new Error('Product reassignment did not return the updated record.');
  }
  return updated;
}

export async function saveDraftPrintRun(payload: unknown): Promise<any> {
  const response = await axios.post('/printrun/draft/save', payload);
  return throwIfLegacyResultError(response.data, 'Failed to save draft');
}

export async function removeDraftPrintRun(
  organizationId: number | string,
  printRunId: number | string
): Promise<any> {
  const response = await axios.delete('/printrun/remove', {
    params: { organizationId, printRunId },
  });
  return response.data;
}

export function useDeleteDraftPrintRun(
  organizationId?: number | string,
): UseDeleteDraftPrintRunResult {
  const [isDeletingDraft, setIsDeletingDraft] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const deleteDraft = useCallback<DeleteDraftPrintRun>(async (printRunId) => {
    if (!organizationId) throw new Error('Unable to delete draft');

    const deletionKey = `${organizationId}:${printRunId}`;
    let deletionRequest = draftDeletionRequests.get(deletionKey);
    if (!deletionRequest) {
      deletionRequest = removeDraftPrintRun(organizationId, printRunId);
      draftDeletionRequests.set(deletionKey, deletionRequest);
      void deletionRequest.finally(() => {
        if (draftDeletionRequests.get(deletionKey) === deletionRequest) {
          draftDeletionRequests.delete(deletionKey);
        }
      }).catch(() => undefined);
    }

    setIsDeletingDraft(true);
    try {
      await deletionRequest;
    } finally {
      if (isMountedRef.current) setIsDeletingDraft(false);
    }
  }, [organizationId]);

  return { deleteDraft, isDeletingDraft };
}

export async function recordPrintRunError(
  printRunId: number | string,
  payload: unknown
): Promise<any> {
  const response = await axios.post(`/printrun/${printRunId}/error`, payload);
  return response.data;
}
